import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { sanitizeText, safeUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const BGNSpotlightSection = () => {
  const [spotlight, setSpotlight] = useState(null);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/spotlight`)
      .then(r => r.json())
      .then(data => setSpotlight(data.spotlight || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !spotlight) return null;

  const name        = sanitizeText(spotlight.name);
  const title       = sanitizeText(spotlight.title);
  const bio         = sanitizeText(spotlight.bio);
  const quote       = sanitizeText(spotlight.quote);
  const imageUrl    = safeUrl(spotlight.image_url);
  const achievements = Array.isArray(spotlight.achievements) ? spotlight.achievements : [];

  const stats = [
    { value: spotlight.stat1_value, label: spotlight.stat1_label },
    { value: spotlight.stat2_value, label: spotlight.stat2_label },
    { value: spotlight.stat3_value, label: spotlight.stat3_label },
  ].filter(s => s.value && s.label);

  const socials = [
    { key: 'twitch',    url: spotlight.social_twitch,    label: 'Twitch' },
    { key: 'twitter',   url: spotlight.social_twitter,   label: 'Twitter' },
    { key: 'youtube',   url: spotlight.social_youtube,   label: 'YouTube' },
    { key: 'instagram', url: spotlight.social_instagram, label: 'Instagram' },
    { key: 'tiktok',    url: spotlight.social_tiktok,    label: 'TikTok' },
    { key: 'website',   url: spotlight.social_website,   label: 'Website' },
  ].filter(s => s.url);

  return (
    <section className="section-padding bg-white" id="spotlight">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-12">
          <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-2">BGN Spotlight</p>
          <h2 className="heading-large text-black lowercase">Community Spotlight</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-gray-200">

          {/* Image */}
          <div className="relative bg-gray-100 min-h-80">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={name}
                className="w-full h-full object-cover object-top"
                style={{ minHeight: 320 }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-200 min-h-80">
                <span className="text-gray-400 text-6xl font-bold">{name[0]?.toUpperCase()}</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-10 flex flex-col justify-between bg-white">
            <div>
              <h3 className="heading-medium text-black mb-1">{name}</h3>
              {title && <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-6">{title}</p>}

              {quote && (
                <blockquote className="border-l-4 border-black pl-4 mb-6">
                  <p className="body-large text-gray-700 italic leading-relaxed">"{quote}"</p>
                </blockquote>
              )}

              {bio && <p className="body-small text-gray-600 leading-relaxed mb-6">{bio}</p>}

              {achievements.length > 0 && (
                <ul className="space-y-2 mb-6">
                  {achievements.map((a, i) => (
                    <li key={i} className="flex items-start space-x-2 text-sm text-gray-700">
                      <span className="text-black font-bold mt-0.5">—</span>
                      <span>{sanitizeText(String(a))}</span>
                    </li>
                  ))}
                </ul>
              )}

              {stats.length > 0 && (
                <div className="grid grid-cols-3 gap-4 border-t border-gray-100 pt-6 mb-6">
                  {stats.map((s, i) => (
                    <div key={i} className="text-center">
                      <p className="heading-small text-black">{sanitizeText(s.value)}</p>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mt-1">{sanitizeText(s.label)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {socials.length > 0 && (
              <div className="flex flex-wrap gap-3 border-t border-gray-100 pt-6">
                {socials.map(s => {
                  const href = safeUrl(s.url);
                  if (!href) return null;
                  return (
                    <a
                      key={s.key}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-xs font-medium uppercase tracking-wide text-gray-600 hover:text-black border border-gray-200 px-3 py-2 hover:border-black transition-colors duration-200"
                    >
                      <span>{s.label}</span>
                      <ExternalLink size={10} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </section>
  );
};

export default BGNSpotlightSection;
